// src/components/Sidebar/MobileDrawer.jsx
//
// El menú completo en móvil. La barra de abajo se queda con los cinco destinos
// de todos los días; acá está todo lo demás, incluido el cambio de cuenta y la
// salida, que en escritorio cuelgan del avatar del rail.
//
// Se dibuja como panel deslizante y no como pantalla completa a propósito: la
// franja de la derecha deja ver dónde estabas, y tocarla cierra. Es la salida
// que la gente busca antes que la X.
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Check,
  LogOut,
  Search,
  Settings as SettingsIcon,
  User,
  X,
} from "lucide-react";
import { RAIL_DESTINATIONS, RAIL_UTILITIES, SEPARATOR_AFTER } from "../../lib/navigation";
import { useCommandPalette } from "../CommandPalette/CommandPaletteContext";
import { useOrganizations } from "../../hooks/useOrganizations";
import AuthContext from "../../AuthContext";
import logo from "../../assets/Logo.webp";
import defaultAvatar from "../../assets/avatar.webp";
import { cn } from "../../lib/utils";

function DrawerRow({ Icon, label, isActive, onClick, trailing }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex h-12 w-full items-center gap-3 rounded-2xl px-3 text-left transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
        isActive ? "bg-[#1C1431] text-white" : "text-white/80 active:bg-white/10"
      )}
    >
      {Icon && <Icon className="h-5 w-5 shrink-0" strokeWidth={2} />}
      <span
        className={cn("min-w-0 flex-1 truncate text-[15px]", isActive && "font-semibold")}
      >
        {label}
      </span>
      {trailing}
    </button>
  );
}

export function MobileDrawer({ open, onClose, active, onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const { openPalette } = useCommandPalette();
  const { user, logout } = React.useContext(AuthContext);
  const { currentContext, personalAccount, organizations = [], changeContext } =
    useOrganizations();

  const userName = user?.username || user?.name || "My Account";
  const userEmail = user?.email || "";
  const userAvatar = user?.avatar_url || user?.avatarUrl || defaultAvatar;
  const isPersonalActive = currentContext?.type === "PERSONAL";

  // Con el cajón abierto, el fondo no debe desplazarse: en iOS el scroll se
  // "cuela" al body y al cerrar apareces en otro punto de la página.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // El foco entra al panel al abrir: sin esto el teclado y el lector de
  // pantalla se quedan en el botón de atrás, detrás del velo.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const go = (id) => {
    onSelect(id);
    onClose();
  };

  const goTo = (path) => {
    navigate(path);
    onClose();
  };

  const switchContext = async (nextContext) => {
    try {
      await changeContext(nextContext);
    } catch (error) {
      console.error("Failed to switch context", error);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[75] md:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label={t("sidebar.closeMenu", "Close menu")}
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-black/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "absolute inset-y-0 left-0 flex w-[86%] max-w-[20rem] flex-col",
          "border-r border-white/12 bg-[#0A0518] shadow-[0_0_60px_rgba(0,0,0,0.7)]",
          "animate-reveal-left focus:outline-none"
        )}
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <button
            type="button"
            onClick={() => go("Zeno")}
            className="rounded-lg p-1 transition active:bg-white/10"
            aria-label="Go to Zeno"
          >
            <img src={logo} alt="Zenit" className="h-9 w-auto" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("sidebar.closeMenu", "Close menu")}
            className={cn(
              "inline-flex h-11 w-11 items-center justify-center rounded-2xl",
              "text-white/60 transition active:bg-white/10 active:text-white",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
            )}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => {
              onClose();
              openPalette();
            }}
            className={cn(
              "flex h-11 w-full items-center gap-2.5 rounded-2xl px-3",
              "border border-white/10 bg-white/[0.04] text-white/50 transition",
              "active:bg-white/[0.1] active:text-white/80",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
            )}
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left text-sm">
              {t("palette.open", "Search")}
            </span>
          </button>
        </div>

        <nav
          aria-label={t("sidebar.navLabel", "Main")}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2"
        >
          <ul className="space-y-1">
            {RAIL_DESTINATIONS.map((entry) => (
              <React.Fragment key={entry.id}>
                <li>
                  <DrawerRow
                    Icon={entry.Icon}
                    label={t(entry.i18nKey, entry.label)}
                    isActive={entry.id === active}
                    onClick={() => go(entry.id)}
                  />
                </li>
                {SEPARATOR_AFTER.has(entry.id) && (
                  <li role="presentation" aria-hidden="true" className="py-1">
                    <hr className="border-0 border-t border-white/10" />
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>

          <hr className="my-2 border-0 border-t border-white/10" />

          <ul className="space-y-1">
            {RAIL_UTILITIES.map((entry) => (
              <li key={entry.id}>
                <DrawerRow
                  Icon={entry.Icon}
                  label={t(entry.i18nKey, entry.label)}
                  isActive={entry.id === active}
                  onClick={() => go(entry.id)}
                />
              </li>
            ))}
          </ul>

          <hr className="my-2 border-0 border-t border-white/10" />

          <p className="px-3 pb-1 pt-1 text-[11px] uppercase tracking-wide text-gray-500">
            {t("sidebar.workspace", "Workspace")}
          </p>
          <ul className="space-y-1">
            <li>
              <DrawerRow
                Icon={User}
                label={t("sidebar.personal", "Personal")}
                onClick={() =>
                  switchContext({ type: "PERSONAL", id: personalAccount?.id })
                }
                trailing={
                  isPersonalActive ? (
                    <Check className="h-4 w-4 shrink-0 text-purple-300" />
                  ) : null
                }
              />
            </li>
            {organizations.map((org) => (
              <li key={org.id}>
                <DrawerRow
                  Icon={Building2}
                  label={org.name}
                  onClick={() => switchContext({ type: "ORGANIZATION", id: org.id })}
                  trailing={
                    currentContext?.type === "ORGANIZATION" &&
                    currentContext?.id === org.id ? (
                      <Check className="h-4 w-4 shrink-0 text-purple-300" />
                    ) : null
                  }
                />
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-white/10 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2">
          <div className="flex items-center gap-3 px-3 py-2">
            <img
              src={userAvatar}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full border border-white/20 object-cover"
              onError={(e) => {
                e.target.src = defaultAvatar;
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{userName}</div>
              {userEmail ? (
                <div className="truncate text-xs text-gray-400">{userEmail}</div>
              ) : null}
            </div>
          </div>

          <ul className="space-y-1">
            <li>
              <DrawerRow
                Icon={Building2}
                label={t("sidebar.organizations", "Organizations")}
                onClick={() => goTo("/organizations")}
              />
            </li>
            <li>
              <DrawerRow
                Icon={SettingsIcon}
                label={t("sidebar.settings", "Settings")}
                onClick={() => goTo("/u/settings/profile")}
              />
            </li>
            <li>
              <DrawerRow
                Icon={LogOut}
                label={t("sidebar.logout", "Logout")}
                onClick={() => {
                  onClose();
                  logout();
                }}
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MobileDrawer;
